"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { AnsiPanel } from "@/components/tools/random-fun/discord-ansi-editor";
import {
  OutputBox,
  PanelHeading,
} from "@/components/tools/random-fun/discord-output";
import {
  PreviewCode,
  PreviewLink,
  PreviewShell,
  StyledLine,
} from "@/components/tools/random-fun/discord-preview";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type BlockKind,
  CODE_LANGUAGES,
  type CodeLanguage,
  codeBlock,
  formatText,
  type InlineStyles,
  inlineCode,
  maskedLink,
  NO_STYLES,
} from "@/lib/tool-kit/random-fun/discord-markdown";

/** English strings. A translated content file overrides these through `ui`. */
export const DISCORD_TEXT_FORMATTER_STRINGS = {
  modeLabel: "What to make",
  modeStyle: "Text styles",
  modeLink: "Masked link",
  modeCode: "Code",
  modeAnsi: "Coloured text (ANSI)",
  inputLabel: "Your text",
  inputPlaceholder: "Type your message. Each line is formatted separately.",
  sampleStyle: "Game night\nBring snacks and a friend",
  styleHeading: "Text styles",
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  strike: "Strikethrough",
  spoiler: "Spoiler",
  blockLabel: "Line format",
  blockNone: "Normal text",
  blockH1: "Heading 1 (#)",
  blockH2: "Heading 2 (##)",
  blockH3: "Heading 3 (###)",
  blockSubtext: "Subtext (-#)",
  blockBullets: "Bulleted list (-)",
  blockNumbers: "Numbered list (1.)",
  blockQuote: "Quote on each line (>)",
  blockQuoteBlock: "Quote everything after the first line marker (>>>)",
  escape: "Show symbols such as * _ ~ | and ` exactly as typed",
  blockNote:
    "Headings, subtext, lists and quotes only work at the start of a line, and the text after the marker needs a space.",
  linkHeading: "Masked link",
  linkTextLabel: "Link text",
  linkTextPlaceholder: "Our rules",
  linkUrlLabel: "Web address",
  linkUrlPlaceholder: "https://example.com/rules",
  linkNote:
    "The reader sees only the link text and goes to the address when they click it. Use it for links you trust readers to expect.",
  problemEmptyText: "Enter the text people should see.",
  problemEmptyUrl: "Enter the web address.",
  problemNotHttp:
    "The address must start with http:// or https:// and have no spaces.",
  codeHeading: "Code",
  codeTextLabel: "Your code or text",
  codeTextPlaceholder: "console.log('hello');",
  codeKindLabel: "Kind",
  codeInline: "Inline code",
  codeFenced: "Code block",
  languageLabel: "Language for colouring",
  languageNone: "None",
  languageAnsi: "ansi (coloured text, see the next tab)",
  codeNote:
    "Formatting does not work inside code, and a spoiler around it is ignored. A code block that is not in a recognised language shows without colours.",
  ansiHeading: "Coloured text in a code block",
  ansiNote:
    "ANSI colours are not part of the Markdown in Discord’s own help article. They show in the desktop and web apps, but the phone apps have not shown them reliably, so test on your own devices before relying on them.",
  escapeNote:
    "The ␛ symbol stands for the invisible escape character. The copy button includes the real character.",
  segmentText: "Text",
  segmentLabel: "Piece {number}",
  textColour: "Text colour",
  background: "Background",
  defaultColour: "Default",
  remove: "Remove",
  removeLabel: "Remove piece {number}",
  add: "Add a piece of text",
  sampleOne: "Colour",
  sampleTwo: " your message",
  foregroundNames: {
    "30": "Gray",
    "31": "Red",
    "32": "Green",
    "33": "Yellow",
    "34": "Blue",
    "35": "Pink",
    "36": "Cyan",
    "37": "White",
  },
  backgroundNames: {
    "40": "Dark blue",
    "41": "Orange",
    "42": "Blue-gray",
    "43": "Turquoise",
    "44": "Gray",
    "45": "Indigo",
    "46": "Light gray",
    "47": "White",
  },
  previewLabel: "Preview",
  previewNote:
    "An approximation of Discord’s dark theme. Colours and spacing in the real app differ, and spoilers reveal when clicked.",
  spoilerLabel: "Spoiler. Click to show or hide.",
  outputLabel: "Paste this into Discord",
  copy: "Copy",
  copied: "Copied!",
  characters: "{count} characters",
  limitNote:
    "Discord lets most accounts send {limit} characters in one message. Split this into parts.",
  emptyOutput: "Your formatted text will appear here.",
} as const;

type Strings = typeof DISCORD_TEXT_FORMATTER_STRINGS;
type Mode = "style" | "link" | "code" | "ansi";

const BLOCK_KEY = {
  none: "blockNone",
  h1: "blockH1",
  h2: "blockH2",
  h3: "blockH3",
  subtext: "blockSubtext",
  bullets: "blockBullets",
  numbers: "blockNumbers",
  quote: "blockQuote",
  quoteBlock: "blockQuoteBlock",
} as const;
const BLOCKS = Object.keys(BLOCK_KEY) as BlockKind[];

const PROBLEM_KEY = {
  emptyText: "problemEmptyText",
  emptyUrl: "problemEmptyUrl",
  notHttp: "problemNotHttp",
} as const;

const LINE_BREAK = /\r?\n/;

function StylePanel({ strings }: { strings: Strings }) {
  const [text, setText] = useState<string>(strings.sampleStyle);
  const [styles, setStyles] = useState<InlineStyles>({
    ...NO_STYLES,
    bold: true,
  });
  const [block, setBlock] = useState<BlockKind>("h2");
  const [escapeText, setEscapeText] = useState(false);
  const output = formatText(text, styles, block, escapeText);
  const toggle = (key: keyof InlineStyles) => (on: boolean) =>
    setStyles({ ...styles, [key]: on });
  let counter = 0;
  return (
    <div className="space-y-5">
      <PanelHeading>{strings.styleHeading}</PanelHeading>
      <TextAreaField
        id="dtf-style-text"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={4}
        value={text}
      />
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        <Checkbox
          checked={styles.bold}
          label={strings.bold}
          onChange={toggle("bold")}
        />
        <Checkbox
          checked={styles.italic}
          label={strings.italic}
          onChange={toggle("italic")}
        />
        <Checkbox
          checked={styles.underline}
          label={strings.underline}
          onChange={toggle("underline")}
        />
        <Checkbox
          checked={styles.strike}
          label={strings.strike}
          onChange={toggle("strike")}
        />
        <Checkbox
          checked={styles.spoiler}
          label={strings.spoiler}
          onChange={toggle("spoiler")}
        />
      </div>
      <SelectField
        hint={strings.blockNote}
        id="dtf-block"
        label={strings.blockLabel}
        onChange={setBlock}
        options={BLOCKS.map((value) => ({
          value,
          label: strings[BLOCK_KEY[value]],
        }))}
        value={block}
      />
      <Checkbox
        checked={escapeText}
        label={strings.escape}
        onChange={setEscapeText}
      />
      <PreviewShell label={strings.previewLabel}>
        {text.split(LINE_BREAK).map((line, index) => {
          if (line.trim() !== "") {
            counter += 1;
          }
          return (
            <StyledLine
              block={block}
              // Lines are positional and may repeat.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
              key={index}
              number={counter}
              spoilerLabel={strings.spoilerLabel}
              styles={styles}
              text={line}
            />
          );
        })}
      </PreviewShell>
      <p className={HINT}>{strings.previewNote}</p>
      <OutputBox
        copyText={output}
        id="dtf-style-output"
        shown={output}
        strings={strings}
      />
    </div>
  );
}

function LinkPanel({ strings }: { strings: Strings }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const result = maskedLink(label, url);
  const markdown = "markdown" in result ? result.markdown : "";
  const touched = label !== "" || url !== "";
  return (
    <div className="space-y-5">
      <PanelHeading>{strings.linkHeading}</PanelHeading>
      <TextField
        id="dtf-link-text"
        label={strings.linkTextLabel}
        onChange={setLabel}
        placeholder={strings.linkTextPlaceholder}
        value={label}
      />
      <TextField
        id="dtf-link-url"
        label={strings.linkUrlLabel}
        onChange={setUrl}
        placeholder={strings.linkUrlPlaceholder}
        type="url"
        value={url}
      />
      <p className={HINT}>{strings.linkNote}</p>
      {touched && "problem" in result ? (
        <ErrorNotice>{strings[PROBLEM_KEY[result.problem]]}</ErrorNotice>
      ) : null}
      {markdown ? (
        <PreviewShell label={strings.previewLabel}>
          <PreviewLink text={label.trim()} url={url.trim()} />
        </PreviewShell>
      ) : null}
      <OutputBox
        copyText={markdown}
        id="dtf-link-output"
        shown={markdown}
        strings={strings}
      />
    </div>
  );
}

function CodePanel({ strings }: { strings: Strings }) {
  const [text, setText] = useState("");
  const [fenced, setFenced] = useState("block");
  const [language, setLanguage] = useState<CodeLanguage>("");
  const isBlock = fenced === "block";
  const output = buildCode(text, isBlock, language);
  return (
    <div className="space-y-5">
      <PanelHeading>{strings.codeHeading}</PanelHeading>
      <TextAreaField
        id="dtf-code-text"
        label={strings.codeTextLabel}
        onChange={setText}
        placeholder={strings.codeTextPlaceholder}
        rows={4}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.codeKindLabel}
          name="dtf-code-kind"
          onChange={setFenced}
          options={[
            { value: "block", label: strings.codeFenced },
            { value: "inline", label: strings.codeInline },
          ]}
          value={fenced}
        />
        {isBlock ? (
          <SelectField
            id="dtf-language"
            label={strings.languageLabel}
            onChange={setLanguage}
            options={CODE_LANGUAGES.map((value) => ({
              value,
              label: languageLabel(value, strings),
            }))}
            value={language}
          />
        ) : null}
      </div>
      <p className={HINT}>{strings.codeNote}</p>
      {output ? (
        <PreviewShell label={strings.previewLabel}>
          <PreviewCode block={isBlock} text={text.trim()} />
        </PreviewShell>
      ) : null}
      <OutputBox
        copyText={output}
        id="dtf-code-output"
        shown={output}
        strings={strings}
      />
    </div>
  );
}

function buildCode(
  text: string,
  block: boolean,
  language: CodeLanguage
): string {
  if (!block) {
    return inlineCode(text.trim());
  }
  return text.trim() === "" ? "" : codeBlock(text, language);
}

function languageLabel(value: CodeLanguage, strings: Strings): string {
  if (value === "") {
    return strings.languageNone;
  }
  return value === "ansi" ? strings.languageAnsi : value;
}

export default function DiscordTextFormatterWidget() {
  const strings = useToolUi(DISCORD_TEXT_FORMATTER_STRINGS);
  const [mode, setMode] = useState<Mode>("style");
  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="dtf-mode"
        onChange={setMode}
        options={[
          { value: "style", label: strings.modeStyle },
          { value: "link", label: strings.modeLink },
          { value: "code", label: strings.modeCode },
          { value: "ansi", label: strings.modeAnsi },
        ]}
        value={mode}
      />
      <div hidden={mode !== "style"}>
        <StylePanel strings={strings} />
      </div>
      <div hidden={mode !== "link"}>
        <LinkPanel strings={strings} />
      </div>
      <div hidden={mode !== "code"}>
        <CodePanel strings={strings} />
      </div>
      <div hidden={mode !== "ansi"}>
        <AnsiPanel strings={strings} />
      </div>
    </div>
  );
}
